
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { namespace } from "vuex-class"
import CalculationDataType from "@/types/field"

import { CaseColumn } from "@/types/caseColumns"
import {
	CalculationType,
	CalculationSortItemType,
	DataType,
	Insight,
	InsightCalculation,
	InsightCalculationTotal,
	Operator,
	operatorLabels,
	InsightDetails,
	InsightSummary
} from "@/types/insights"

// @ts-ignore
import draggable from "vuedraggable"
import vSelect from "vue-select"

import VuePerfectScrollbar from "vue-perfect-scrollbar"

import Calculation from "./Calculations/Calculation.vue"
import CalculationTotal from "./Calculations/CalculationTotal.vue"
import CalculationTotals from "./CalculationTotals.vue"
import CalculationExpression from "./CalculationExpression.vue"

import api from "@/services/api"
import Guid from "@/scripts/guid"
import { InsightCalculationItem, TimeSpentCalculation } from "@/types/insights/calculation"

import { UserRole } from "@/types/caseUser"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"calculation": Calculation,
		"calculation-total": CalculationTotal,
		"calculation-totals": CalculationTotals,
		"calculation-expression": CalculationExpression,
		"help-icon": HelpIcon,
		draggable
	}
})
export default class InsightCalculations extends Vue {
	@Prop({ default: null })
	insight!: InsightDetails

	@Prop({ default: () => [] })
	insights!: InsightSummary[]

	@Prop()
	fields!: CaseColumn[]

	@Prop({ default: () => {} })
	cachedFetchedInsights!: { [key: string]: Insight | null }

	@Prop({ default: () => () => null })
	fetchInsightAndCache!: (insightId: string) => null

	filtersExpanded: boolean = false;
	operators = operatorLabels;

	calculationsValid: boolean = true
	private Operator = Operator;

	loaded: boolean = false;

	calculationTotalsColumns: {key: string, label: string}[] = [
		{
			key: "title",
			label: "Title"
		},
		{
			key: "groupBy",
			label: "Group By"
		},
		{
			key: "calculationList",
			label: "Calculations"
		},
		{
			key: "subtractCalculationList",
			label: "Subtract Calculations"
		},
		{
			key: "expression",
			label: "Expression"
		},
		{
			key: "buttons",
			label: ""
		}
	]

	queryColumns = [
		{
			key: "field",
			label: "Field"
		},
		{
			key: "operator",
			label: "Operator"
		},
		{
			key: "value",
			label: "Value"
		},
		{
			key: "buttons",
			label: ""
		}
	]

	calculationColumns = [
		{
			key: "label",
			label: "Title"
		},
		{
			key: "calcType",
			label: "Calculation"
		},
		{
			key: "field",
			label: "Field"
		},
		{
			key: "groupBy",
			label: "Group By"
		},
		{
			key: "expression",
			label: "Expression"
		},
		{
			key: "buttons",
			label: ""
		}
	]

	dragOptions = {
		animation: 200,
		group: "calculations",
		disabled: false,
		ghostClass: "ghost"
	}

	private CalculationType = CalculationType;
	private CalculationSortItemType = CalculationSortItemType

	calculationTypeValues = [
		{
			type: CalculationType.Total,
			label: "Total",
			requiresField: true
		},
		{
			type: CalculationType.Count,
			label: "Count",
			requiresField: false
		},
		{
			type: CalculationType.Average,
			label: "Average",
			requiresField: true
		},
		{
			type: CalculationType.Min,
			label: "Minimum",
			requiresField: true
		},
		{
			type: CalculationType.Max,
			label: "Maximum",
			requiresField: true
		},
		{
			type: CalculationType.CountPerDay,
			label: "Count Per Day",
			requiresField: false,
			canUse: () => this.insight.clauses.some(c => c.fieldDataType === DataType.dateTime && c.operator === Operator.GreaterThan)
		}
	]

	get calculationTypes (): any[] {
		return this.calculationTypeValues.filter(calcType => calcType.canUse === undefined || calcType.canUse())
	}

	get groupByCalculationFields (): CaseColumn[] {
		if (!this.fields) {
			return []
		}

		const groupableDataTypes = [DataType.boolean, DataType.number, DataType.dateTime, DataType.array, DataType.arrayMultiple, DataType.userRole]
		return this.fields.filter(f => groupableDataTypes.includes(f.fieldDataType!))
	}

	get calculationFields () {
		if (!this.fields) {
			return []
		}

		return this.fields.filter(f =>
			f.dataType === "number" ||
			f.dataType === "dollar" ||
			(f.dataType === "calculated" && this.calculatedFieldSupportsInsightCalculations(f)) ||
			(f.dataType === "user-role" && f.fieldExtensions && (f.fieldExtensions.allowTimeTracking ||
				(f.fieldExtensions.subroles && f.fieldExtensions.subroles.some((r: UserRole) => r.allowTimeTracking)))))
	}

	setCalculationsValid (calculationValid: boolean) {
		this.calculationsValid = calculationValid
	}

	private calculatedFieldSupportsInsightCalculations (field: CaseColumn): Boolean {
		if (!field.fieldExtensions) {
			return false
		}

		const calculationDataType = this.getCalculationDataType(field)
		return calculationDataType === "number" || calculationDataType === "time-spent"
	}

	private getCalculationDataTypeFromFieldId (fieldId: string) : string | null {
		const strippedFieldId = this.stripFieldId(fieldId)

		const field = this.getFieldFromFieldId(strippedFieldId)
		if (!field || !field.fieldExtensions) {
			return null
		}
		const calculationDataType = CalculationDataType[field.fieldExtensions.calculationType as CalculationType]
		return calculationDataType
	}

	private stripFieldId (fieldId: string) : string {
		if (fieldId.startsWith("fields.")) {
			return fieldId.substring(7)
		}
		return fieldId
	}

	private getFieldFromFieldId (fieldId: string) : CaseColumn {
		const strippedFieldId = this.stripFieldId(fieldId)
		const field = this.fields.find(c => c.fieldId === strippedFieldId)
		if (!field) {
			Error(`Field ${fieldId} not found`)
		}
		return field!
	}

	private getTimeSpentTrackingField (timeSpentFieldId: string) : CaseColumn {
		const strippedFieldId = this.stripFieldId(timeSpentFieldId)
		const timeSpentField = this.fields.find(c => c.fieldId === strippedFieldId)

		const trackingFieldId = timeSpentField!.fieldExtensions!.trackingFieldId
		const trackingField = this.fields.find(c => c.fieldId === trackingFieldId)

		if (!trackingField) {
			throw new Error(`Could not find tracking field ${trackingFieldId}`)
		}
		return trackingField
	}

	private timeSpentCalculationChange (
		calculation: InsightCalculation,
		timeSpentCalculation: TimeSpentCalculation) {
		calculation.timeSpentCalculation = timeSpentCalculation
	}

	private getCalculationDataType (field: CaseColumn) : string | null {
		if (!field.fieldExtensions) {
			return null
		}
		const calculationDataType = CalculationDataType[field.fieldExtensions.calculationType as CalculationType]
		return calculationDataType
	}

	get includableInsights (): InsightSummary[] {
		if (!this.insight.groupBy) {
			return this.insights.filter(insight => insight.id !== this.insight.id &&
			insight.hasCalculations && !insight.groupBy)
		} else {
			const groupBy = this.fields.find(f => f.key === this.insight.groupBy)
			if (!groupBy) {
				return []
			}

			const dataTypeMatch = (groupBy.dataType === "date-time" || groupBy.dataType === "date")
				? (f: CaseColumn) => f.dataType === "date-time" || f.dataType === "date"
				: (f: CaseColumn) => f.dataType === groupBy.dataType
			const allowedGroupBys = new Set<string>(this.fields.filter(dataTypeMatch).map(field => field.key))

			return this.insights.filter(insight => insight.id !== this.insight.id &&
				insight.hasCalculations && allowedGroupBys.has(insight.groupBy!))
		}
	}

	get calculationNumberColumns () {
		return this.insight.calculations.map(c => {
			return {
				label: c.label,
				key: `calculations[${c.key}]`,
				fieldDataType: DataType.number
			}
		})
	}

	get calculationItems (): InsightCalculationItem<InsightCalculation | InsightCalculationTotal | null>[] {
		const calculationItems = this.insight.calculations.map(c => {
			return {
				key: c.key,
				type: CalculationSortItemType.Calculation,
				item: c,
				settings: {
					hasExpression: !!c.expression,
					hasGrouping: c.groupBy !== null
				}
			} as InsightCalculationItem<InsightCalculation>
		})
		const calculationTotalItems = this.insight.calculationTotals
			? this.insight.calculationTotals.map(c => {
				return {
					key: c.key,
					type: CalculationSortItemType.CalculationTotal,
					item: c,
					settings: {
						hasExpression: !!c.expression
					}
				} as InsightCalculationItem<InsightCalculationTotal>
			}) : []
		const includedInsightCalculationItems = this.insight.includeCalculationsFrom
			? this.insight.includeCalculationsFrom.map(i => {
				const includedInsight = this.cachedFetchedInsights[i]
				if (!includedInsight) return []
				return includedInsight.calculations.map(c => {
					return {
						key: c.key,
						label: c.label,
						type: CalculationSortItemType.IncludedCalculation,
						settings: {
							insightTitle: includedInsight.title
						}
					} as InsightCalculationItem<null>
				})
			}).flat()
			: []

		const sort = this.insight.calculationSort || []

		const calculationsByKey = calculationItems.toDictionary("key")
		const calculationsTotalsByKey = calculationTotalItems.toDictionary("key")
		const includedItemsByKey = includedInsightCalculationItems.toDictionary("key")

		const sortedItems = sort.map(key => {
			if (calculationsByKey[key]) {
				return calculationsByKey[key]
			} else if (calculationsTotalsByKey[key]) {
				return calculationsTotalsByKey[key]
			} else if (includedItemsByKey[key]) {
				return includedItemsByKey[key]
			}
			return null
		}).filter(i => i !== null) as InsightCalculationItem<InsightCalculation | InsightCalculationTotal | null>[]

		const unsortedItems = [
			...calculationItems.filter(c => !sort.includes(c.key)),
			...calculationTotalItems.filter(c => !sort.includes(c.key)),
			...includedInsightCalculationItems.filter(c => !sort.includes(c.key))
		]

		return [
			...sortedItems,
			...unsortedItems
		]
	}

	isCalcValid (calculation: InsightCalculation): boolean {
		if (!calculation.label ||
			(calculation.calcType === undefined || calculation.calcType === null) ||
			(calculation.calcType !== CalculationType.Count && calculation.calcType !== CalculationType.CountPerDay && !calculation.field)
		) {
			return false
		}
		return true
	}

	@Watch("insight.calculations", { deep: true })
	calcWatcher () {
		if (this.insight.calculations &&
				this.insight.calculations.map(calc => this.isCalcValid(calc)).includes(false) &&
				this.insightsCalculationTotalsValid()) {
			this.calculationsValid = false
		} else {
			this.calculationsValid = true
		}
	}

	addCalculation () {
		if (this.insight && !this.insight.calculations) {
			this.insight.calculations = []
		}
		if (this.insight.calculations && (this.insight.calculations.length === 0 || this.calculationsValid)) {
			this.insight.calculations.push({
				fieldId: "",
				key: Guid.newGuid(),
				label: "",
				formattingRules: [],
				groupBy: null,
				expression: null
			})
		}
	}

	private addCalculationTotal (): void {
		if (this.insight) {
			if (!this.insight.calculationTotals) {
				this.insight.calculationTotals = []
			}

			this.insight.calculationTotals.push({
				key: Guid.newGuid(),
				title: "",
				calculationList: [],
				subtractCalculationList: [],
				groupBy: null,
				expression: null,
				collate: false
			})
		}
	}

	removeCalculation (calculationItemType: CalculationSortItemType, calculation: InsightCalculation | InsightCalculationTotal) {
		if (calculationItemType === CalculationSortItemType.Calculation) {
			this.insight.calculations.splice(this.insight.calculations.indexOf(calculation as InsightCalculation), 1)

			// Remove calculation from any calculation totals
			if (this.insight.calculationTotals && this.insight.calculationTotals.length > 0) {
				this.insight.calculationTotals.forEach(c => {
					c.calculationList.remove(calculation.key)
					c.subtractCalculationList.remove(calculation.key)
				})
			}
		} else if (calculationItemType === CalculationSortItemType.CalculationTotal) {
			this.insight.calculationTotals!.splice(this.insight.calculationTotals!.indexOf(calculation as InsightCalculationTotal), 1)
		}
	}

	showCalculationFormattingRules (calculation: InsightCalculation | null) {
		this.$emit("showFormat", calculation)
	}

	getInsightDetails (insightId: string) {
		if (!this.cachedFetchedInsights[insightId]) {
			this.cachedFetchedInsights[insightId] = null
			api.insightGet(insightId).then(insightDetails => {
				this.cachedFetchedInsights[insightId] = insightDetails
			})
		}

		return this.cachedFetchedInsights[insightId]
	}

	private get insightsTotalFields (): InsightCalculation[] {
		if (this.insight && this.insight.calculations) {
			return this.insight.calculations.filter((c: { label: string }) => c.label)
		}
		return []
	}

	/**
	 * Used to specify when insights calculation totals should be shown.
	 * @returns true if there is at least 1 calculation total, false otherwise.
	 */
	private get insightsTotalShown (): boolean {
		return this.insightsTotalFields.length > 0
	}

	/**
	 * List of all calculations including those from "includeCalculationsFrom"
	 */
	private get allCalculations (): InsightCalculation[] {
		return [
			...this.insight.calculations,
			...(this.insight.includeCalculationsFrom
				? this.insight.includeCalculationsFrom.map(insightId => {
					const insight = this.getInsightDetails(insightId)
					return insight && insight.calculations ? insight.calculations : []
				}).flat()
				: [])
		]
	}

	/**
	 * Checks that the insight calculation totals are valid.
	 * @returns True if the list of calculation totals are filled in (i.e. have a title and >1 calculation). False otherwise.
	 * @private
	 */
	private insightsCalculationTotalsValid (): boolean {
		if (this.insight.calculationTotals) {
			return !this.insight.calculationTotals.some((t: InsightCalculationTotal) => !t.title || t.calculationList.length === 0 || t.expression !== "")
		}
		return true
	}

	private checkCalculationValidity () {
		this.insight.calculations.filter(calculation => !this.calculationTypes.find(calcType => calcType.type === calculation.calcType))
			.forEach(calculation => calculation.calcType = null)
	}

	private expressionCheckChanged (calculation: InsightCalculation, hasExpression: boolean) {
		if (hasExpression) {
			if (!calculation.expression) {
				calculation.expression = "{value}"
			}
		} else {
			calculation.expression = null
		}
	}

	private groupByCheckChanged (calculation: InsightCalculation, hasExpression: boolean) {
		if (hasExpression) {
			if (!calculation.groupBy) {
				calculation.groupBy = ""
			}
		} else {
			calculation.groupBy = null
		}
	}

	private onInput (args: InsightCalculationItem<InsightCalculation | InsightCalculationTotal | null>[]) {
		if (this.insight) {
			this.insight.calculationSort = args.map(i => i.key)
		}
	}
}

